import React from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Footer } from "@/components/Footer";
import { Nav } from "@/components/Nav";
import { ScrollToTop } from "@/components/ScrollToTop";
import { useLanguage } from "@/hooks/useLanguage";
import { useTheme } from "@/hooks/useTheme";
import { About } from "@/pages/About";
import { Book } from "@/pages/Book";
import { Concepts } from "@/pages/Concepts";
import { Contact } from "@/pages/Contact";
import { English } from "@/pages/English";
import { Home } from "@/pages/Home";
import { Legacy } from "@/pages/Legacy";
import { PostDetail } from "@/pages/PostDetail";
import { Posts } from "@/pages/Posts";
import { Videos } from "@/pages/Videos";

// Admin chunk: lazy-loaded and gated by Cloudflare Access at the edge,
// so it never reaches the browser unless the visitor is already
// authenticated. Lazy-importing keeps the public bundle free of admin
// code (and the larger admin deps it pulls in).
const AdminShell = React.lazy(() => import("@/admin/AdminShell"));

const App: React.FC = () => {
  const language = useLanguage();
  const theme = useTheme();
  const { t, isRTL } = language;
  const location = useLocation();

  // The admin SPA owns its own layout (no public Nav/Footer). Detect
  // the path early so we can skip the public chrome entirely under
  // /admin/*.
  const isAdmin = location.pathname.startsWith("/admin");

  if (isAdmin) {
    return (
      <div
        dir="ltr"
        lang="en"
        className="ff-en-body"
        style={{
          background: "var(--ink)",
          color: "var(--parchment)",
          minHeight: "100vh",
        }}
      >
        <ScrollToTop />
        <React.Suspense
          fallback={
            <div className="p-10 text-sm opacity-60 ff-en-body">
              Loading admin…
            </div>
          }
        >
          <Routes>
            <Route
              path="/admin/*"
              element={<AdminShell language={language} />}
            />
          </Routes>
        </React.Suspense>
      </div>
    );
  }

  return (
    <div
      dir={t.dir}
      lang={language.lang}
      className={isRTL ? "ff-ar-sans" : "ff-en-body"}
      style={{ background: "var(--ink)", color: "var(--parchment)", minHeight: "100vh" }}
    >
      <ScrollToTop />
      <Nav language={language} theme={theme} />
      <main key={location.pathname} className="fade-in">
        <Routes>
          <Route path="/" element={<Home language={language} />} />
          <Route path="/book" element={<Book language={language} />} />
          <Route path="/concepts" element={<Concepts language={language} />} />
          <Route path="/videos" element={<Videos language={language} />} />
          <Route path="/posts" element={<Posts language={language} />} />
          <Route path="/posts/:slug" element={<PostDetail language={language} />} />
          <Route path="/english" element={<English language={language} />} />
          <Route path="/about" element={<About language={language} />} />
          <Route path="/legacy" element={<Legacy language={language} />} />
          <Route path="/contact" element={<Contact language={language} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer language={language} />
    </div>
  );
};

export default App;
